import PortalProfileSwitcher from "./PortalProfileSwitcher";
import { portalContextRoute } from "../../lib/portalContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  isNewClient,
  intakeIsComplete,
  isLoggedIn,
  logout,
} from "../../hooks/usePortalAuth";
import {
  hasAdminAccess,
  hasClinicalAccess,
  hasStaffAccess,
  hasMarketingAccess,
  canAccessSupervisorDashboard,
  getUserDisplayName,
  getRoleNames,
  isSuperAdmin,
} from "../../lib/auth";
import {
  DESIGN_PREVIEW_MODE,
  getPreviewRole,
  setPreviewRole,
  getPreviewDefaultRoute,
  previewGetVisibleSections,
  PREVIEW_ROLES,
  type PreviewRole,
} from "../../lib/previewMode";
import PracticeLogo from "./PracticeLogo";
import NotificationBell from "./NotificationBell";
import { apiGetAdminMessageUnreadCount, apiGetCurrentUser, apiGetPortalDashboardSummary, apiGetPortalMessageUnreadCount, apiGetPortalProfiles, apiGetStaffMessageUnreadCount, apiListGroupServiceEpisodes, apiListSupervisionLogRelationships, apiSwitchPortalProfile, PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT, type PortalProfileChoice } from "../../lib/api";

interface PortalNavProps {
  isAdminProp?: boolean;
}

type NavItem = {
  label: string;
  to: string;
  count?: number;
};

type NavGroup = {
  id: "clinical" | "supervision" | "admin" | "marketing" | "scheduling" | "operations" | "more";
  label: string;
  items: NavItem[];
};

function dedupeNavItems(items: NavItem[]): NavItem[] {
  const seenLabels = new Set<string>();
  const seenTargets = new Set<string>();

  return items.filter((item) => {
    const labelKey = item.label.trim().toLowerCase();
    const targetKey = item.to.trim().toLowerCase();
    if (!labelKey || !targetKey || seenLabels.has(labelKey) || seenTargets.has(targetKey)) {
      return false;
    }
    seenLabels.add(labelKey);
    seenTargets.add(targetKey);
    return true;
  });
}

function isActiveRoute(currentPath: string, currentSearch: string, target: string): boolean {
  const [pathWithSearch] = target.split("#");
  const [targetPath, targetSearch = ""] = pathWithSearch.split("?");
  if (currentPath !== targetPath && !currentPath.startsWith(`${targetPath}/`)) return false;
  return !targetSearch || currentSearch === `?${targetSearch}`;
}

function resolveStaffMessagesItem({
  showClient,
  showAdmin,
  showStaff,
  showCalendar,
  staffUnreadCount,
  administrativeMessageAccess,
}: {
  showClient: boolean;
  showAdmin: boolean;
  showStaff: boolean;
  showCalendar: boolean;
  staffUnreadCount?: number;
  administrativeMessageAccess: boolean | null;
}): NavItem | null {
  if (showClient) return null;
  if (showAdmin && administrativeMessageAccess === null) return null;
  if (showAdmin && administrativeMessageAccess) return { label: "Messages", to: "/admin/messages", count: staffUnreadCount };
  if (showStaff || showCalendar) return { label: "Messages", to: "/staff/messages", count: staffUnreadCount };
  return null;
}

export default function PortalNav({ isAdminProp = false }: PortalNavProps) {
  const [, setIdentityRevision] = useState(0);
  useEffect(() => {
    const refreshIdentity = () => setIdentityRevision((revision) => revision + 1);
    window.addEventListener("portalUserProfileChanged", refreshIdentity);
    return () => window.removeEventListener("portalUserProfileChanged", refreshIdentity);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [mobileRoleOpen, setMobileRoleOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState<NavGroup["id"] | null>(null);
  const [roleRevision, setTick] = useState(0);
  const [profileChoices, setProfileChoices] = useState<PortalProfileChoice[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [activePortalProfile, setActivePortalProfile] = useState<PortalProfileChoice | null>(null);
  const [profileState, setProfileState] = useState<"loading" | "ready" | "switching" | "error">("loading");
  const [navigationCounts, setNavigationCounts] = useState<Record<string, number>>({});
  const [canViewOperationsAnalytics, setCanViewOperationsAnalytics] = useState(false);
  const [administrativeMessageAccess, setAdministrativeMessageAccess] = useState<boolean | null>(() => DESIGN_PREVIEW_MODE ? hasAdminAccess() : null);
  const [hasSupervisionRelationship, setHasSupervisionRelationship] = useState(false);
  const [canManageSupervision,setCanManageSupervision]=useState(canAccessSupervisorDashboard());
  const [hasAssignedGroups, setHasAssignedGroups] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const navDropdownRef = useRef<HTMLDivElement>(null);

  // Close role dropdown on outside click
  useEffect(() => {
    if (!roleDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target as Node)) {
        setRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [roleDropdownOpen]);

  // Close grouped nav dropdowns on outside click
  useEffect(() => {
    if (!openNavDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(e.target as Node)) {
        setOpenNavDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openNavDropdown]);

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenNavDropdown(null);
  }, [location.pathname, location.search, location.hash]);

  // Keep in sync when preview or cached portal roles change.
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    if (DESIGN_PREVIEW_MODE) {
      window.addEventListener("previewRoleChanged", refresh);
    }
    window.addEventListener("portalRolesChanged", refresh);
    return () => {
      window.removeEventListener("previewRoleChanged", refresh);
      window.removeEventListener("portalRolesChanged", refresh);
    };
  }, []);
  useEffect(() => { if(DESIGN_PREVIEW_MODE)return;setCanViewOperationsAnalytics(false);setAdministrativeMessageAccess(null);if (!isLoggedIn() || !hasStaffAccess()) return; let active=true;const refresh=()=>void apiGetCurrentUser().then((response) => {if(!active)return;const permissions=response.data?.operations_permissions||[];setCanViewOperationsAnalytics(permissions.includes("operations.access"));setAdministrativeMessageAccess(response.data?.administrative_message_access===true);});refresh();window.addEventListener("focus",refresh);return()=>{active=false;window.removeEventListener("focus",refresh);}; }, [roleRevision]);
  useEffect(() => {
    setHasSupervisionRelationship(false);
    setCanManageSupervision(canAccessSupervisorDashboard());
    if (!isLoggedIn() || DESIGN_PREVIEW_MODE || !hasClinicalAccess() || canAccessSupervisorDashboard()) return;
    let active = true;
    void apiListSupervisionLogRelationships().then((response) => {
      if (active) {setHasSupervisionRelationship(Boolean(response.data?.relationships?.length));setCanManageSupervision(Boolean(response.data?.relationships?.some(relationship=>relationship.can_manage_logs)));}
    });
    return () => { active = false; };
  }, [roleRevision]);

  // --- Section visibility ---
  // Preview mode uses the role matrix; real auth uses actual helpers
  // Auth helpers read the current role cache, which changes independently of props.
  const sections = (() => {
    if (DESIGN_PREVIEW_MODE) {
      return { ...previewGetVisibleSections(), showCalendar: false };
    }
    const superadminUser = isSuperAdmin();
    return {
      showClient: !hasStaffAccess() && !hasMarketingAccess(),
      showAdmin: isAdminProp || hasAdminAccess(),
      // SuperAdmins keep admin access and may also see linked staff clinical/supervisor sections.
      showStaff: hasClinicalAccess(),
      showCalendar: hasStaffAccess() && !hasAdminAccess(),
      showSupervision: canAccessSupervisorDashboard() || hasSupervisionRelationship,
      showMarketing: hasMarketingAccess(),
      showRoleAdmin: isSuperAdmin() || superadminUser,
    };
  })();

  const { showClient, showAdmin, showStaff, showSupervision, showMarketing, showCalendar } = sections;

  useEffect(() => {
    if (!showStaff || DESIGN_PREVIEW_MODE) { setHasAssignedGroups(false); return; }
    let cancelled = false;
    void apiListGroupServiceEpisodes().then((response) => {
      if (!cancelled) setHasAssignedGroups(Boolean(response.data?.groups?.length));
    });
    return () => { cancelled = true; };
  }, [showStaff]);

  useEffect(() => {
    if (!showClient || DESIGN_PREVIEW_MODE) { setProfileState("ready"); return; }
    let cancelled = false;
    void apiGetPortalProfiles().then((response) => {
      if (cancelled) return;
      if (!response.data || !Array.isArray(response.data.profiles) || !response.data.active_profile?.client_profile_id) {
        setProfileChoices([]);
        setActiveProfileId(null);
        setActivePortalProfile(null);
        setProfileState("error");
        return;
      }
      setProfileChoices(response.data.profiles);
      setActiveProfileId(String(response.data.active_profile.client_profile_id));
      setActivePortalProfile({...response.data.profiles.find(profile=>String(profile.client_profile_id)===String(response.data!.active_profile.client_profile_id)),...response.data.active_profile});
      setProfileState("ready");
      // Activation must never block the routes needed to finish it. Billing
      // also owns Stripe return URLs; redirecting unmounts the secure element.
      const destination=portalContextRoute(response.data.active_profile,location.pathname);
      if(destination!==location.pathname)navigate(destination,{replace:true});
    });
    return () => { cancelled = true; };
  }, [location.pathname, navigate, showClient]);

  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) return;
    let cancelled = false;
    let inFlight = false;
    let invalidated = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleCounts = () => { if (!refreshTimer && !inFlight && !cancelled) refreshTimer = setTimeout(() => {refreshTimer=undefined;void refreshCounts();},100); };
    const refreshCounts = async () => {
      if (cancelled || inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      invalidated = false;
      try {
      if (showClient && activePortalProfile) {
        const [summary, unread] = await Promise.all([
          activePortalProfile?.capabilities?.dashboard !== false ? apiGetPortalDashboardSummary() : null,
          activePortalProfile?.capabilities?.messages !== false ? apiGetPortalMessageUnreadCount() : null,
        ]);
        if (!cancelled && !invalidated && summary?.data && (!summary.data.active_profile || String(summary.data.active_profile.client_profile_id) === String(activePortalProfile.client_profile_id))) setNavigationCounts((current) => ({ ...summary.data!.counts, messages: current.messages ?? summary.data!.counts.messages }));
        if (!cancelled && unread?.data) setNavigationCounts((current) => ({ ...current, messages: unread.data!.count }));
      } else if (showAdmin || showStaff || showCalendar) {
        if (showAdmin && administrativeMessageAccess === null) return;
        const unreadRequest = administrativeMessageAccess ? apiGetAdminMessageUnreadCount() : apiGetStaffMessageUnreadCount();
        const response = await unreadRequest;
        if (!cancelled && response.data) setNavigationCounts((current) => ({ ...current, staffMessages: response.data!.count }));
      }
      } finally { inFlight = false; if (invalidated && !cancelled) scheduleCounts(); }
    };
    void refreshCounts();
    const pollId = window.setInterval(() => { void refreshCounts(); }, 120_000);
    const onInvalidated = () => { invalidated = true; scheduleCounts(); };
    window.addEventListener(PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT, onInvalidated);
    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      window.clearInterval(pollId);
      window.removeEventListener(PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT, onInvalidated);
    };
  }, [showAdmin, showCalendar, showClient, showStaff, activePortalProfile, administrativeMessageAccess]);

  const handleProfileSwitch = async (clientProfileId: string) => {
    if (clientProfileId === activeProfileId || profileState === "switching") return;
    setProfileState("switching");
    const response = await apiSwitchPortalProfile(clientProfileId);
    if (!response.data) { setProfileState("error"); return; }
    setActiveProfileId(String(response.data.active_profile.client_profile_id));
    window.dispatchEvent(new CustomEvent("portal-profile-changed"));
    // A document reload guarantees that no component cache from the prior chart survives.
    const destination=portalContextRoute(response.data.active_profile,location.pathname);
    const nextSearch=new URLSearchParams(location.search);
    if(location.pathname==="/clientportal/intake"){nextSearch.delete("client");nextSearch.delete("form");}
    const query=nextSearch.toString();
    window.location.assign(destination===location.pathname?destination+(query?`?${query}`:"")+location.hash:destination);
  };

  // Current active section (for highlighting)
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const inAdmin = currentPath.startsWith("/admin");
  const inStaff = currentPath.startsWith("/staff") && !currentPath.startsWith("/staff/supervision");
  const inSupervision = currentPath.startsWith("/staff/supervision");
  const inMarketing = currentPath.startsWith("/marketing");

  // Preview display info
  const previewRoleInfo = DESIGN_PREVIEW_MODE
    ? PREVIEW_ROLES.find((r) => r.role === getPreviewRole())
    : null;

  const handleRoleSwitch = useCallback(
    (role: PreviewRole) => {
      setPreviewRole(role);
      setRoleDropdownOpen(false);
      const defaultRoute = getPreviewDefaultRoute();
      const currentSection = "/" + currentPath.split("/").filter(Boolean)[0];
      const defaultSection = "/" + defaultRoute.split("/").filter(Boolean)[0];
      if (currentSection !== defaultSection) {
        navigate(defaultRoute, { replace: true });
      } else {
        navigate(location.pathname, { replace: true });
      }
    },
    [navigate, currentPath, location.pathname]
  );

  const displayName = DESIGN_PREVIEW_MODE
    ? (previewRoleInfo?.label ?? "Preview User")
    : getUserDisplayName();
  const displayInitial = displayName.charAt(0).toUpperCase();

  // Client nav items (only when in client-only view)
  const showIntake = showClient && activePortalProfile?.setup_applicable !== false && isNewClient() && !intakeIsComplete();
  const clientCapabilities = activePortalProfile?.capabilities || {};
  const showPortalSettings = activePortalProfile?.relationship_type === "payer" ? clientCapabilities.settings === true : true;
  const clientNavItems = showClient && (DESIGN_PREVIEW_MODE || activePortalProfile) ? dedupeNavItems([
    ...(clientCapabilities.dashboard === false ? [] : [{ label: showIntake && clientCapabilities.intake !== false ? "Intake" : "Dashboard", to: showIntake && clientCapabilities.intake !== false ? "/clientportal/intake" : "/clientportal/dashboard", count: showIntake ? navigationCounts.forms : undefined }]),
    ...(clientCapabilities.appointments === false ? [] : [{ label: "Appointments", to: "/clientportal/appointments", count: navigationCounts.appointments }]),
    ...(!showIntake && clientCapabilities.intake !== false ? [{ label: "Forms", to: "/clientportal/intake", count: navigationCounts.forms }] : []),
    ...(showIntake || clientCapabilities.documents === false ? [] : [{ label: "Documents", to: "/clientportal/documents", count: undefined }]),
    ...(clientCapabilities.assessments === false ? [] : [{ label: "Assessments", to: "/clientportal/measures" }]),
    ...(clientCapabilities.surveys === false ? [] : [{ label: "Surveys", to: "/clientportal/surveys" }]),
    ...(clientCapabilities.billing === false ? [] : [{ label: "Billing", to: "/clientportal/billing" }]),
    ...(clientCapabilities.messages === false ? [] : [{ label: "Messages", to: "/clientportal/messages", count: navigationCounts.messages }]),
    ...(clientCapabilities.resources === false ? [] : [{ label: "Resources", to: "/clientportal/resources", count: (navigationCounts.resources || 0) + (navigationCounts.tasks || 0) }]),
    ...(showPortalSettings ? [{ label: "Settings", to: "/clientportal/settings" }] : []),
  ]) : [];

  const primaryClientItems=clientNavItems.filter(item=>["Dashboard","Intake","Forms","Appointments","Documents","Billing","Messages"].includes(item.label));
  const secondaryClientItems=clientNavItems.filter(item=>!primaryClientItems.includes(item));
  const dashboardItem = useMemo<NavItem | null>(() => {
    if (showClient) return null;
    if (showAdmin) return { label: "Dashboard", to: "/admin" };
    if (showStaff) return { label: "Dashboard", to: "/staff" };
    if (showCalendar) return { label: "Calendar", to: "/staff/availability" };
    if (showSupervision) return { label: "Dashboard", to: "/staff/supervision" };
    if (showMarketing) return { label: "Dashboard", to: "/marketing" };
    return null;
  }, [showAdmin, showCalendar, showClient, showMarketing, showStaff, showSupervision]);

  const staffMessagesItem = useMemo<NavItem | null>(() => {
    return resolveStaffMessagesItem({
      showClient,
      showAdmin,
      showStaff,
      showCalendar,
      staffUnreadCount: navigationCounts.staffMessages,
      administrativeMessageAccess,
    });
  }, [administrativeMessageAccess, navigationCounts.staffMessages, showAdmin, showCalendar, showClient, showStaff]);

  const navGroups = useMemo<NavGroup[]>(() => {
    const groups: NavGroup[] = [];

    if (showCalendar && !showStaff) {
      groups.push({ id: "scheduling", label: "Scheduling", items: [{ label: "Calendar", to: "/staff/availability" }] });
    }

    if (showStaff) {
      groups.push({
        id: "clinical",
        label: "Clinical",
        items: dedupeNavItems([
          ...(!showAdmin ? [{ label: "Calendar", to: "/staff/availability" }] : []),
          { label: "Caseload", to: "/staff/clients" },
          ...(hasAssignedGroups ? [{ label: "Group Members", to: "/staff/groups" }] : []),
          { label: "Resources", to: "/staff/resources" },
          { label: "Clinical Architect", to: "/staff/clinicalarchitect" },
          { label: "My Analytics", to: "/staff/analytics?scope=self" },
        ]),
      });
    }


    if (showSupervision) {
      groups.push({
        id: "supervision",
        label: "Supervision",
        items: dedupeNavItems([
          ...(canManageSupervision?[{ label: "Review Queue", to: "/staff/supervision#review-queue" },{ label: "Signatures", to: "/staff/supervision#signatures" },{ label: "Supervisees", to: "/staff/supervision#supervisees" }]:[]),
          { label: "Supervision Logs", to: "/staff/supervision?tab=logs#supervision-logs" },
          { label: "Supervision Analytics", to: "/staff/analytics?scope=supervision" },
        ]),
      });
    }

    if (showAdmin) {
      groups.push({
        id: "admin",
        label: "Admin",
        items: dedupeNavItems([
          { label: "Booking Requests", to: "/admin/appointments" },
          { label: "Clients", to: "/admin/clients", count: navigationCounts.onboardingAttention },
          { label: "Staff", to: "/admin/staff-profiles" },
          { label: "Calendar & Availability", to: "/admin/availability" },
          { label: "Forms", to: "/admin/forms" },
          { label: "Resources", to: "/staff/resources" },
          { label: "Billing", to: "/admin/billing", count: navigationCounts.billingAttention },
          { label: "Activity Log", to: "/admin/activity-log" },
          { label: "Practice Settings", to: sections.showRoleAdmin ? "/admin/practice-settings" : "/admin/practice-settings/services" },
        ]),
      });
    }

    if (!showClient && (showAdmin || showStaff || showCalendar || showMarketing)) groups.push({ id: "operations", label: "Operations", items: [
      ...(canViewOperationsAnalytics ? [{ label: "Operations Workspace", to: "/admin/operations?tab=overview" }] : []),
      { label: "Tasks", to: "/staff/tasks" },
      { label: "Surveys", to: "/staff/internal-surveys" },
      ...(canViewOperationsAnalytics ? [{ label: "Operations Analytics", to: "/staff/analytics?scope=operations" }] : []),
      { label: "Standard Operating Procedures", to: "/staff/standard-operating-procedures" },
      ...((showStaff || showAdmin || showCalendar) ? [{ label: "Hour Tracker", to: "/admin/operations?tab=hour-tracker" }] : []),
    ] });

    if (showMarketing) {
      groups.push({
        id: "marketing",
        label: "Marketing",
        items: [{ label: "Integration status", to: "/marketing" }],
      });
    }

    return groups.filter((group) => group.items.length > 0);
  }, [canManageSupervision, canViewOperationsAnalytics, hasAssignedGroups, navigationCounts.billingAttention, navigationCounts.onboardingAttention, sections.showRoleAdmin, showAdmin, showCalendar, showClient, showMarketing, showStaff, showSupervision]);

  // Logo destination: highest-priority section
  const logoHref = useMemo(() => {
    if (showAdmin) return "/admin";
    if (showStaff) return "/staff";
    if (showCalendar) return "/staff/availability";
    if (showSupervision) return "/staff/supervision";
    if (showMarketing) return "/marketing";
    return "/clientportal/dashboard";
  }, [showAdmin, showCalendar, showStaff, showSupervision, showMarketing]);

  const handleNav = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    setOpenNavDropdown(null);
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try { await logout(); } finally { setSigningOut(false); }
    navigate("/clientportal", { replace: true });
    setMobileOpen(false);
  };

  // ── Section nav button helper ──────────────────────────────
  const SectionBtn = ({
    label,
    to,
    active,
    subtle,
    count,
  }: {
    label: string;
    to: string;
    active?: boolean;
    subtle?: boolean;
    count?: number;
  }) => (
    <button
      onClick={() => handleNav(to)}
      className={`font-manrope text-[14px] whitespace-nowrap px-2.5 py-1.5 rounded-full transition-colors cursor-pointer ${
        active
          ? "font-semibold text-brand-dark bg-brand-dark/8"
          : subtle
          ? "font-medium text-brand-dark/55 hover:bg-brand-dark/5 hover:text-brand-dark/80"
          : "font-semibold text-brand-dark/70 hover:bg-brand-dark/5"
      }`}
    >
      {label}{Boolean(count) && <span className="ml-1 rounded-full bg-brand-dark/10 px-1.5 text-[10px]" aria-label={`${count} items`}>{count}</span>}
    </button>
  );

  const DropdownBtn = ({ group }: { group: NavGroup }) => {
    const isOpen = openNavDropdown === group.id;
    const active =
      (group.id === "more" && group.items.some(item=>isActiveRoute(currentPath,currentSearch,item.to))) ||
      (group.id === "admin" && inAdmin) ||
      (group.id === "scheduling" && currentPath === "/staff/availability") ||
      (group.id === "clinical" && inStaff) ||
      (group.id === "supervision" && inSupervision) ||
      (group.id === "marketing" && inMarketing);

    return (
      <div className="relative">
        <button
          onClick={() => setOpenNavDropdown(isOpen ? null : group.id)}
          className={`font-manrope text-[14px] whitespace-nowrap px-3 py-1.5 rounded-full transition-colors cursor-pointer inline-flex items-center gap-1 ${
            active || isOpen
              ? "font-semibold text-brand-dark bg-brand-dark/8"
              : "font-semibold text-brand-dark/70 hover:bg-brand-dark/5"
          }`}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {group.label}{group.id==="more"&&group.items.some(item=>item.count)?<span className="rounded-full bg-brand-dark/10 px-1.5 text-[10px]">{group.items.reduce((sum,item)=>sum+(item.count||0),0)}</span>:null}
          <i className={`ri-arrow-down-s-line text-[12px] transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="absolute left-1/2 top-full z-50 mt-2 w-56 -translate-x-1/2 overflow-hidden rounded-2xl border border-brand-border bg-white shadow-lg">
            <div className="px-3 py-2 border-b border-brand-border/70">
              <p className="font-manrope text-[10px] font-bold uppercase tracking-widest text-brand-dark/30">{group.label}</p>
            </div>
            <div className="py-1.5" role="menu">
              {group.items.map((item) => {
                const isActive = isActiveRoute(currentPath, currentSearch, item.to);
                return (
                  <button
                    key={`${group.id}-${item.to}`}
                    onClick={() => handleNav(item.to)}
                    className={`w-full text-left px-3 py-2.5 font-manrope text-[13px] transition-colors cursor-pointer ${
                      isActive
                        ? "bg-brand-dark/5 font-semibold text-brand-dark"
                        : "font-medium text-brand-dark/65 hover:bg-brand-dark/5 hover:text-brand-dark"
                    }`}
                    role="menuitem"
                  >
                    {item.label}{Boolean(item.count) && <span className="ml-1 rounded-full bg-brand-dark/10 px-1.5 text-[10px]" aria-label={`${item.count} items`}>{item.count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <header data-portal-navigation className="fixed top-0 left-0 right-0 z-50 bg-brand-bg/90 backdrop-blur-md border-b border-brand-border">
      <nav className="w-full px-3 md:px-6 xl:px-8 h-[58px] flex items-center justify-between gap-2">

        {/* Logo */}
        <a href={logoHref} className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <PracticeLogo size="header" showText className={showClient?"[&>span]:hidden sm:[&>span]:inline sm:[&>span]:max-w-28 sm:[&>span]:truncate 2xl:[&>span]:max-w-none":""} />
        </a>

        {/* Desktop nav — centered */}
        <div className="hidden xl:flex items-center gap-0.5 flex-1 justify-center min-w-0" ref={navDropdownRef}>

          {/* Client-only nav */}
          {primaryClientItems.map((item) => (
            <SectionBtn
              key={item.to}
              label={item.label}
              to={item.to}
              active={currentPath === item.to || currentPath.startsWith(item.to + "/")}
              subtle
              count={item.count}
            />
          ))}

          {secondaryClientItems.length>0&&<DropdownBtn group={{id:"more",label:"More",items:secondaryClientItems}} />}

          {/* Role-aware grouped nav */}
          {dashboardItem && (
            <SectionBtn
              label={dashboardItem.label}
              to={dashboardItem.to}
              active={currentPath === dashboardItem.to && !currentSearch}
            />
          )}
          {navGroups.map((group) => (
            <DropdownBtn key={group.id} group={group} />
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 shrink-0">
          {showClient && !DESIGN_PREVIEW_MODE && <PortalProfileSwitcher profiles={profileChoices} active={activePortalProfile} disabled={profileState==="switching"} onSelect={id=>void handleProfileSwitch(id)} />}
          {showClient && !DESIGN_PREVIEW_MODE && profileState === "error" && (
            <span role="status" className="hidden lg:inline font-manrope text-[11px] text-rose-700">Profile access changed. Refresh to continue.</span>
          )}
          {/* User badge or preview role switcher */}
          {DESIGN_PREVIEW_MODE ? (
            <div className="relative" ref={roleDropdownRef}>
              <button
                onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-dark/5 hover:bg-brand-dark/8 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className={`w-5 h-5 flex items-center justify-center rounded-full border ${previewRoleInfo?.color ?? "bg-amber-50 border-amber-200 text-amber-700"}`}>
                  <i className={`${previewRoleInfo?.icon ?? "ri-user-line"} text-[9px]`} />
                </div>
                <span className="font-manrope text-[11px] font-semibold text-brand-dark/70">
                  {previewRoleInfo?.shortLabel ?? "Super Admin"}
                </span>
                <span className="font-manrope text-[8px] font-bold text-amber-600 bg-amber-100 px-1 py-0.5 rounded uppercase tracking-wide">
                  Design Preview
                </span>
                <i className={`ri-arrow-down-s-line text-[10px] text-brand-dark/30 transition-transform ${roleDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {roleDropdownOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-60 bg-white border border-neutral-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-neutral-100">
                    <p className="font-manrope text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Preview Role</p>
                  </div>
                  <div className="py-1 max-h-[260px] overflow-y-auto">
                    {PREVIEW_ROLES.map((item) => {
                      const isActive = item.role === getPreviewRole();
                      return (
                        <button
                          key={item.role}
                          onClick={() => handleRoleSwitch(item.role)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors cursor-pointer ${
                            isActive ? "bg-brand-dark/5" : "hover:bg-neutral-50"
                          }`}
                        >
                          <div className={`w-7 h-7 flex items-center justify-center rounded-lg border flex-shrink-0 ${item.color}`}>
                            <i className={`${item.icon} text-[11px]`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-manrope text-[12px] font-semibold text-neutral-800">{item.label}</p>
                            <p className="font-manrope text-[10px] text-neutral-400 leading-snug truncate">{item.description}</p>
                          </div>
                          {isActive && (
                            <i className="ri-check-line text-brand-dark text-xs flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : isLoggedIn() ? (
            <div className="hidden 2xl:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-dark/5">
              <div className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-blue/10">
                <span className="font-manrope text-[9px] font-bold text-brand-blue">
                  {displayInitial}
                </span>
              </div>
              <span className="font-manrope text-[11px] text-brand-dark/60 truncate max-w-[110px]">
                {displayName}
              </span>
            </div>
          ) : null}

          {/* Settings gear — only for non-client roles (clients already have Settings in main nav) */}
          {!showClient && (
          <button
            onClick={() => handleNav("/account/settings")}
            className="hidden xl:flex w-8 h-8 items-center justify-center rounded-full hover:bg-brand-dark/5 transition-colors cursor-pointer"
            aria-label="Settings"
          >
            <i className="ri-settings-3-line text-brand-dark/40 hover:text-brand-dark/70 text-sm transition-colors" />
          </button>
          )}

          {/* Notification bell */}
          {staffMessagesItem && (
            <div className="hidden xl:block">
              <SectionBtn
                label={staffMessagesItem.label}
                to={staffMessagesItem.to}
                active={isActiveRoute(currentPath, currentSearch, staffMessagesItem.to)}
                subtle
                count={staffMessagesItem.count}
              />
            </div>
          )}
          {(showAdmin || showStaff || showCalendar) && <NotificationBell />}

          {/* Sign out */}
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="hidden xl:block font-manrope text-[14px] font-semibold text-brand-dark/55 hover:text-brand-dark/80 transition-colors cursor-pointer whitespace-nowrap"
          >
            {signingOut ? "Signing out…" : "Sign Out"}
          </button>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="xl:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-brand-dark/5 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg text-brand-dark`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-brand-bg border-t border-brand-border px-5 py-4 space-y-0.5 max-h-[calc(100dvh-58px)] overflow-y-auto">

          {/* User info banner */}
          {DESIGN_PREVIEW_MODE ? (
            <div className="mb-2">
              <button
                onClick={() => setMobileRoleOpen(!mobileRoleOpen)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-brand-dark/3 hover:bg-brand-dark/5 transition-colors cursor-pointer"
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-full border ${previewRoleInfo?.color ?? "bg-amber-50 border-amber-200 text-amber-700"}`}>
                  <i className={`${previewRoleInfo?.icon ?? "ri-user-line"} text-[13px]`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-manrope text-[13px] font-semibold text-brand-dark">{previewRoleInfo?.label ?? "Super Admin"}</p>
                  <p className="font-manrope text-[10px] text-amber-500 font-semibold">Tap to switch role</p>
                </div>
                <i className={`ri-arrow-down-s-line text-sm text-brand-dark/30 transition-transform ${mobileRoleOpen ? "rotate-180" : ""}`} />
              </button>

              {mobileRoleOpen && (
                <div className="mt-1.5 space-y-0.5">
                  {PREVIEW_ROLES.map((item) => {
                    const isActive = item.role === getPreviewRole();
                    return (
                      <button
                        key={item.role}
                        onClick={() => {
                          handleRoleSwitch(item.role);
                          setMobileRoleOpen(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors cursor-pointer ${
                          isActive ? "bg-brand-dark/5" : "hover:bg-brand-dark/3"
                        }`}
                      >
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg border flex-shrink-0 ${item.color}`}>
                          <i className={`${item.icon} text-[11px]`} />
                        </div>
                        <span className="font-manrope text-[13px] font-semibold text-neutral-800">{item.label}</span>
                        {isActive && <i className="ri-check-line text-brand-dark text-xs ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : isLoggedIn() ? (
            <div className="flex items-center gap-3 px-3 py-3 mb-2 rounded-xl bg-brand-dark/3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full border bg-brand-blue/10 border-transparent">
                <span className="font-manrope text-[13px] font-bold">{displayInitial}</span>
              </div>
              <div>
                <p className="font-manrope text-[13px] font-semibold text-brand-dark">{displayName}</p>
                <p className="font-manrope text-[11px] text-brand-dark/40">{getRoleNames().join(", ")}</p>
              </div>
            </div>
          ) : null}

          {/* Client nav */}
          {clientNavItems.map((item) => (
            <button
              key={item.to}
              onClick={() => handleNav(item.to)}
              className={`w-full text-left font-manrope text-[14px] font-medium text-brand-dark px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer ${
                currentPath === item.to ? "bg-brand-dark/5 font-semibold" : ""
              }`}
            >
              {item.label}{Boolean(item.count)&&<span className="ml-2 rounded-full bg-brand-dark/10 px-2 text-xs">{item.count}</span>}
            </button>
          ))}

          {/* Role-aware grouped mobile nav */}
          {dashboardItem && (
            <button
              onClick={() => handleNav(dashboardItem.to)}
              className={`w-full text-left font-manrope text-[14px] font-semibold text-brand-dark px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer ${
                currentPath === dashboardItem.to && !currentSearch ? "bg-brand-dark/5" : ""
              }`}
            >
              {dashboardItem.label}
            </button>
          )}

          {staffMessagesItem && (
            <button
              onClick={() => handleNav(staffMessagesItem.to)}
              className={`w-full text-left font-manrope text-[14px] font-semibold text-brand-dark px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer ${
                isActiveRoute(currentPath, currentSearch, staffMessagesItem.to) ? "bg-brand-dark/5" : ""
              }`}
            >
              {staffMessagesItem.label}{Boolean(staffMessagesItem.count) && <span className="ml-1 rounded-full bg-brand-dark/10 px-1.5 text-[10px]" aria-label={`${staffMessagesItem.count} items`}>{staffMessagesItem.count}</span>}
            </button>
          )}

          {navGroups.map((group) => (
            <div key={group.id} className="pt-2">
              <p className="px-3 py-1 font-manrope text-[10px] uppercase tracking-widest text-brand-dark/30">{group.label}</p>
              {group.items.map((item) => (
                <button
                  key={`${group.id}-${item.to}`}
                  onClick={() => handleNav(item.to)}
                  className={`w-full text-left font-manrope text-[14px] font-medium px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer ${
                    isActiveRoute(currentPath, currentSearch, item.to)
                      ? "bg-brand-dark/5 text-brand-dark font-semibold"
                      : "text-brand-dark"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}

          {/* Actions */}
          <div className="pt-3 mt-2 border-t border-brand-border">
            <button
              onClick={() => handleNav(showClient ? "/clientportal/settings" : "/account/settings")}
              className="w-full text-left font-manrope text-[14px] font-medium text-brand-dark/60 px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer"
            >
              <i className="ri-settings-3-line mr-2 text-brand-dark/30" />
              Settings
            </button>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full text-left font-manrope text-[14px] font-semibold text-brand-dark/60 hover:text-brand-dark px-3 py-2.5 rounded-xl hover:bg-brand-dark/5 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line mr-2 text-brand-dark/30" />
              {signingOut ? "Signing out…" : "Sign Out"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
